import React from 'react';

function getScoreColor(score) {
  if (score >= 8) return '#10b981';
  if (score >= 6) return '#3b82f6';
  if (score >= 4) return '#f59e0b';
  return '#ef4444';
}

function ScoreCell({ value, onChange }) {
  const color = getScoreColor(value);
  return (
    <div className="score-cell">
      <div className="score-bubble" style={{ background: color }}>
        {value}
      </div>
      <input
        type="range"
        min="1"
        max="10"
        value={value}
        onChange={e => onChange(parseInt(e.target.value))}
        className="score-slider-mini"
        style={{
          background: `linear-gradient(to right, ${color} ${(value - 1) * 11.1}%, #e5e7eb ${(value - 1) * 11.1}%)`,
        }}
      />
      <style>{`
        .score-slider-mini::-webkit-slider-thumb {
          background: ${color};
        }
      `}</style>
    </div>
  );
}

export default function Step3Subjective({ stepIndicator, schools, factors, scores, setScores, onBack, onNext, hpTheme = false }) {
  const getScore = (school, factorId) => scores[school]?.[factorId] ?? 5;

  const setScore = (school, factorId, value) => {
    setScores(prev => ({
      ...prev,
      [school]: { ...prev[school], [factorId]: value },
    }));
  };

  if (factors.length === 0) {
    return (
      <div className="card">
        {stepIndicator}
        <div className="step-header">
          <h2>{hpTheme ? 'Your Instincts' : 'Step 3 — Your Subjective Ratings'}</h2>
          <p>{hpTheme ? 'No instinctive measures are in play. Very well, we skip ahead to the colder record books.' : "No subjective factors are enabled. All your factors are objective — skip ahead."}</p>
        </div>
        <div className="step-footer">
          <button className="btn btn-secondary" onClick={onBack}>{hpTheme ? '← Back' : '← Back'}</button>
          <button className="btn btn-primary" onClick={onNext}>{hpTheme ? 'Onward →' : 'Continue →'}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      {stepIndicator}
      <div className="step-header">
        <h2>{hpTheme ? 'Tell Me Your True Feelings' : 'Step 3 — Your Subjective Ratings'}</h2>
        <p>
          {hpTheme ? (
            <>Score each school from 1 to 10 on the measures below. These are matters of instinct, not arithmetic. <strong>1 = dreadful fit, 10 = splendid fit.</strong> Trust your first honest impression.</>
          ) : (
            <>Rate each school 1–10 on the factors below. These are subjective — go with your gut. <strong> 1 = very poor fit, 10 = excellent fit.</strong> Don't overthink it.</>
          )}
        </p>
      </div>

      {!hpTheme && (
        <div className="notice notice-info mb-4">
          <strong>Tip:</strong> For factors like "campus culture," base your rating on campus visits, info sessions,
          student conversations, or your overall impression. Honest instincts beat careful analysis here.
        </div>
      )}

      <div className="ratings-table-wrapper">
        <table className="ratings-table">
          <thead>
            <tr>
              <th>{hpTheme ? 'School' : 'School'}</th>
              {factors.map(f => (
                <th key={f.id}>
                  <div>{f.name}</div>
                  <div style={{ fontWeight: 400, color: '#9ca3af', fontSize: '0.7rem', marginTop: '2px' }}>
                    {f.description.length > 50 ? f.description.slice(0, 50) + '…' : f.description}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {schools.map(school => (
              <tr key={school}>
                <td>{school}</td>
                {factors.map(f => (
                  <td key={f.id}>
                    <ScoreCell
                      value={getScore(school, f.id)}
                      onChange={v => setScore(school, f.id, v)}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: '0.75rem' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', fontSize: '0.8rem', color: '#6b7280' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
            <span style={{ background: '#10b981', width: '10px', height: '10px', borderRadius: '50%', display: 'inline-block' }} />
            {hpTheme ? '8-10: A powerful match' : '8–10: Strong fit'}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
            <span style={{ background: '#3b82f6', width: '10px', height: '10px', borderRadius: '50%', display: 'inline-block' }} />
            {hpTheme ? '6-7: Promising' : '6–7: Good fit'}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
            <span style={{ background: '#f59e0b', width: '10px', height: '10px', borderRadius: '50%', display: 'inline-block' }} />
            {hpTheme ? '4-5: Uncertain' : '4–5: Average'}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
            <span style={{ background: '#ef4444', width: '10px', height: '10px', borderRadius: '50%', display: 'inline-block' }} />
            {hpTheme ? '1-3: Ill-suited' : '1–3: Poor fit'}
          </span>
        </div>
      </div>

      <div className="step-footer">
        <button className="btn btn-secondary" onClick={onBack}>{hpTheme ? '← Back To The Weighing' : '← Back'}</button>
        <button className="btn btn-primary" onClick={onNext}>
          {hpTheme ? 'Now For The Record Books →' : 'Continue to Objective Data →'}
        </button>
      </div>
    </div>
  );
}
